import { hasUniqueBlockIds, isBlockNode, isBlockNodeArray, isPigmentJsonValue, } from "./model.js";
import { isThemeConfig } from "./theme.js";
const DOCUMENT_KEYS = new Set(["theme", "blocks", "chrome"]);
const CHROME_SLOTS = ["announcement", "header", "footer"];
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isChromeMode(value) {
    return value === undefined || value === "inherit" || value === "hidden" || value === "custom";
}
export function isChromeOverrides(value) {
    if (value === undefined || value === null)
        return true;
    if (!isRecord(value))
        return false;
    if (Object.keys(value).some((key) => !CHROME_SLOTS.includes(key)))
        return false;
    for (const slot of CHROME_SLOTS) {
        const override = value[slot];
        if (override === undefined)
            continue;
        if (!isRecord(override))
            return false;
        if (Object.keys(override).some((key) => key !== "mode" && key !== "blocks"))
            return false;
        if (!isChromeMode(override.mode))
            return false;
        if (override.blocks !== undefined && !isBlockNodeArray(override.blocks))
            return false;
    }
    return true;
}
export function isPigmentDocument(value) {
    if (!isRecord(value))
        return false;
    if (Object.keys(value).some((key) => !DOCUMENT_KEYS.has(key)))
        return false;
    if (value.theme !== undefined && !isThemeConfig(value.theme))
        return false;
    if (value.blocks !== undefined && !isBlockNodeArray(value.blocks))
        return false;
    return isChromeOverrides(value.chrome);
}
function isNonEmptyText(value) {
    return typeof value === "string" && value.length > 0 && value === value.trim();
}
function isRevision(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function hasOnlyKeys(value, allowed) {
    return Object.keys(value).every((key) => allowed.includes(key));
}
function isPigmentBlockRoot(value) {
    if (!isRecord(value) || typeof value.kind !== "string")
        return false;
    if (value.kind === "blocks")
        return hasOnlyKeys(value, ["kind"]);
    return value.kind === "chrome"
        && hasOnlyKeys(value, ["kind", "slot"])
        && CHROME_SLOTS.includes(value.slot);
}
function isPigmentBlockPlacement(value) {
    if (!isRecord(value) || !hasOnlyKeys(value, ["root", "parentId", "index"]))
        return false;
    if (!isPigmentBlockRoot(value.root))
        return false;
    if (value.parentId !== null && !isNonEmptyText(value.parentId))
        return false;
    return value.index === undefined || (typeof value.index === "number" && Number.isSafeInteger(value.index) && value.index >= 0);
}
function isJsonBlockNode(value) {
    if (!isBlockNode(value) || !hasUniqueBlockIds([value]))
        return false;
    return hasJsonBlockProps(value);
}
function hasJsonBlockProps(node) {
    if (node.props !== undefined && !isPigmentJsonValue(node.props))
        return false;
    return node.children?.every(hasJsonBlockProps) ?? true;
}
function isDocumentOperation(value) {
    if (!isRecord(value) || typeof value.op !== "string")
        return false;
    if (value.op === "set-theme")
        return hasOnlyKeys(value, ["op", "theme"]) && isThemeConfig(value.theme);
    if (value.op === "reset-theme")
        return hasOnlyKeys(value, ["op"]);
    if (value.op === "set-chrome-mode")
        return hasOnlyKeys(value, ["op", "slot", "mode"])
            && CHROME_SLOTS.includes(value.slot)
            && isChromeMode(value.mode)
            && value.mode !== undefined;
    if (value.op === "insert")
        return hasOnlyKeys(value, ["op", "block", "placement"])
            && isJsonBlockNode(value.block) && isPigmentBlockPlacement(value.placement);
    if (value.op === "move")
        return hasOnlyKeys(value, ["op", "id", "placement"])
            && isNonEmptyText(value.id) && isPigmentBlockPlacement(value.placement);
    if (value.op === "remove")
        return hasOnlyKeys(value, ["op", "id"]) && isNonEmptyText(value.id);
    if (value.op === "set")
        return hasOnlyKeys(value, ["op", "id", "key", "value"])
            && isNonEmptyText(value.id) && isNonEmptyText(value.key)
            && Object.hasOwn(value, "value") && isPigmentJsonValue(value.value);
    return value.op === "reset" && hasOnlyKeys(value, ["op", "id", "key"])
        && isNonEmptyText(value.id) && isNonEmptyText(value.key);
}
export function isPigmentDocumentMutation(value) {
    return isRecord(value)
        && hasOnlyKeys(value, ["expectedRevision", "operations"])
        && isRevision(value.expectedRevision)
        && Array.isArray(value.operations)
        && value.operations.every(isDocumentOperation);
}
export function isPigmentDocumentImport(value) {
    return isRecord(value)
        && hasOnlyKeys(value, ["schema", "expectedRevision", "document"])
        && value.schema === "pigment.document-import.v1"
        && isRevision(value.expectedRevision)
        && isPigmentDocument(value.document)
        && hasStableDocumentIds(value.document)
        && hasJsonDocumentProps(value.document);
}
function cloneJson(value) {
    if (Array.isArray(value))
        return value.map(cloneJson);
    if (isRecord(value))
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneJson(item)]));
    return value;
}
function cloneRoot(root) {
    return root.kind === "blocks" ? { kind: "blocks" } : { kind: "chrome", slot: root.slot };
}
function sameRoot(left, right) {
    if (left.kind !== right.kind)
        return false;
    return left.kind === "blocks" || (right.kind === "chrome" && left.slot === right.slot);
}
function cloneBlock(node) {
    return {
        ...node,
        ...(node.props === undefined ? {} : { props: cloneJson(node.props) }),
        ...(node.children === undefined ? {} : { children: node.children.map(cloneBlock) }),
    };
}
function equalJson(left, right) {
    if (left === right)
        return true;
    if (typeof left !== "object" || left === null || typeof right !== "object" || right === null)
        return false;
    if (Array.isArray(left) || Array.isArray(right)) {
        return Array.isArray(left) && Array.isArray(right)
            && left.length === right.length
            && left.every((value, index) => equalJson(value, right[index]));
    }
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    return leftKeys.length === rightKeys.length
        && leftKeys.every((key, index) => key === rightKeys[index] && equalJson(left[key], right[key]));
}
function blocksForRoot(document, root) {
    if (root.kind === "blocks")
        return document.blocks ?? [];
    const override = document.chrome?.[root.slot];
    return override?.mode === "custom" ? override.blocks ?? [] : null;
}
function hasStableDocumentIds(document) {
    return hasUniqueBlockIds([
        ...(document.blocks ?? []),
        ...CHROME_SLOTS.flatMap((slot) => document.chrome?.[slot]?.blocks ?? []),
    ]);
}
function hasJsonDocumentProps(document) {
    return [
        ...(document.blocks ?? []),
        ...CHROME_SLOTS.flatMap((slot) => document.chrome?.[slot]?.blocks ?? []),
    ].every(hasJsonBlockProps);
}
/** P2 editing accepts only documents whose IDs are non-empty and globally unique. */
export function isPigmentRevisionedDocument(value) {
    return isRecord(value)
        && isRevision(value.revision)
        && isPigmentDocument(value.document)
        && hasStableDocumentIds(value.document)
        && hasJsonDocumentProps(value.document);
}
function withRootBlocks(document, root, blocks) {
    if (root.kind === "blocks")
        return { ...document, blocks };
    const override = document.chrome?.[root.slot];
    if (override?.mode !== "custom")
        throw new Error("Cannot write a non-custom chrome root.");
    return {
        ...document,
        chrome: {
            ...(document.chrome ?? {}),
            [root.slot]: { ...override, blocks },
        },
    };
}
function findInNodes(nodes, id, root, parentId = null) {
    for (const [index, node] of nodes.entries()) {
        if (node.id === id)
            return { node, placement: { root: cloneRoot(root), parentId, index } };
        const child = node.children && findInNodes(node.children, id, root, node.id);
        if (child)
            return child;
    }
    return null;
}
function findInRoot(document, root, id) {
    const blocks = blocksForRoot(document, root);
    return blocks ? findInNodes(blocks, id, root) : null;
}
function findLocatedBlock(document, id) {
    const content = findInRoot(document, { kind: "blocks" }, id);
    if (content)
        return content;
    for (const slot of CHROME_SLOTS) {
        const chrome = findInRoot(document, { kind: "chrome", slot }, id);
        if (chrome)
            return chrome;
    }
    return null;
}
function containsId(node, id) {
    return node.id === id || node.children?.some((child) => containsId(child, id)) === true;
}
function blockIds(node, ids = []) {
    ids.push(node.id);
    node.children?.forEach((child) => blockIds(child, ids));
    return ids;
}
function insertIntoNodes(nodes, parentId, child, index) {
    if (parentId === null) {
        if (index > nodes.length)
            return { nodes, inserted: false };
        const next = [...nodes];
        next.splice(index, 0, child);
        return { nodes: next, inserted: true };
    }
    for (const [nodeIndex, node] of nodes.entries()) {
        if (node.id === parentId) {
            const children = node.children ?? [];
            if (index > children.length)
                return { nodes, inserted: false };
            const nextChildren = [...children];
            nextChildren.splice(index, 0, child);
            const next = [...nodes];
            next[nodeIndex] = { ...node, children: nextChildren };
            return { nodes: next, inserted: true };
        }
        if (!node.children)
            continue;
        const nested = insertIntoNodes(node.children, parentId, child, index);
        if (!nested.inserted)
            continue;
        const next = [...nodes];
        next[nodeIndex] = { ...node, children: nested.nodes };
        return { nodes: next, inserted: true };
    }
    return { nodes, inserted: false };
}
function removeFromNodes(nodes, id) {
    for (const [index, node] of nodes.entries()) {
        if (node.id === id) {
            const next = [...nodes];
            const [removed] = next.splice(index, 1);
            return { nodes: next, removed: removed ?? null };
        }
        if (!node.children)
            continue;
        const nested = removeFromNodes(node.children, id);
        if (!nested.removed)
            continue;
        const next = [...nodes];
        next[index] = { ...node, children: nested.nodes };
        return { nodes: next, removed: nested.removed };
    }
    return { nodes, removed: null };
}
function replaceInNodes(nodes, id, replacement) {
    for (const [index, node] of nodes.entries()) {
        if (node.id === id) {
            const next = [...nodes];
            next[index] = replacement;
            return next;
        }
        if (!node.children)
            continue;
        const nested = replaceInNodes(node.children, id, replacement);
        if (!nested)
            continue;
        const next = [...nodes];
        next[index] = { ...node, children: nested };
        return next;
    }
    return null;
}
function resolvedPlacement(placement, siblingCount) {
    const index = placement.index ?? siblingCount;
    if (index > siblingCount)
        return null;
    return { root: cloneRoot(placement.root), parentId: placement.parentId, index };
}
function siblingCount(document, placement) {
    const blocks = blocksForRoot(document, placement.root);
    if (blocks === null)
        return null;
    if (placement.parentId === null)
        return blocks.length;
    const parent = findInRoot(document, placement.root, placement.parentId);
    return parent ? (parent.node.children ?? []).length : null;
}
function invalidState(message) {
    return { ok: false, conflict: { code: "invalid_state", message } };
}
function invalidMutation(message, operationIndex) {
    return { ok: false, conflict: { code: "invalid_mutation", message, ...(operationIndex === undefined ? {} : { operationIndex }) } };
}
/**
 * Applies a fully declarative document edit. It validates the initial state,
 * exact operation shape, all references, IDs, and placements before returning
 * a revisioned result; failed mutations never expose a partially edited tree.
 */
export function applyPigmentDocumentMutation(state, mutation) {
    if (!isPigmentRevisionedDocument(state)) {
        return invalidState("Expected a revisioned Pigment document with globally unique stable block IDs.");
    }
    if (!isPigmentDocumentMutation(mutation)) {
        return invalidMutation("Expected a well-formed Pigment document mutation.");
    }
    if (mutation.expectedRevision !== state.revision) {
        return { ok: false, conflict: { code: "revision_mismatch", expectedRevision: mutation.expectedRevision, actualRevision: state.revision } };
    }
    let document = state.document;
    const diff = [];
    for (const [operationIndex, operation] of mutation.operations.entries()) {
        if (operation.op === "set-theme") {
            const hadPrevious = document.theme !== undefined;
            const previous = document.theme;
            if (previous !== undefined && equalJson(previous, operation.theme))
                continue;
            const theme = cloneJson(operation.theme);
            document = { ...document, theme };
            diff.push({
                op: "set-theme",
                hadPrevious,
                ...(previous === undefined ? {} : { previous: cloneJson(previous) }),
                theme,
            });
            continue;
        }
        if (operation.op === "reset-theme") {
            if (document.theme === undefined)
                continue;
            const previous = document.theme;
            document = { ...document };
            delete document.theme;
            diff.push({ op: "reset-theme", previous: cloneJson(previous) });
            continue;
        }
        if (operation.op === "set-chrome-mode") {
            const chrome = document.chrome ?? {};
            const previous = chrome[operation.slot];
            if (operation.mode !== "custom" && (previous?.blocks?.length ?? 0) > 0) {
                return {
                    ok: false,
                    conflict: {
                        code: "invalid_chrome_transition",
                        slot: operation.slot,
                        operationIndex,
                        message: "Move or remove every custom chrome block before switching the slot away from custom mode.",
                    },
                };
            }
            const next = operation.mode === "custom"
                ? { mode: "custom", blocks: previous?.blocks?.map(cloneBlock) ?? [] }
                : { mode: operation.mode };
            if (previous !== undefined && equalJson(previous, next))
                continue;
            document = { ...document, chrome: { ...chrome, [operation.slot]: next } };
            diff.push({ op: "set-chrome-mode", slot: operation.slot, previousMode: previous?.mode ?? null, mode: operation.mode });
            continue;
        }
        if (operation.op === "insert") {
            const duplicateId = blockIds(operation.block).find((id) => findLocatedBlock(document, id));
            if (duplicateId)
                return { ok: false, conflict: { code: "duplicate_block_id", id: duplicateId, operationIndex } };
            const blocks = blocksForRoot(document, operation.placement.root);
            if (blocks === null) {
                return { ok: false, conflict: { code: "invalid_placement", operationIndex, message: "Chrome mutations require an existing custom chrome slot." } };
            }
            if (operation.placement.parentId !== null && !findInRoot(document, operation.placement.root, operation.placement.parentId)) {
                return { ok: false, conflict: { code: "invalid_placement", operationIndex, message: "The insertion parent must exist in the destination root." } };
            }
            const count = siblingCount(document, operation.placement);
            const placement = count === null ? null : resolvedPlacement(operation.placement, count);
            if (!placement) {
                return { ok: false, conflict: { code: "invalid_placement", operationIndex, message: "The insertion index is outside the destination sibling list." } };
            }
            const inserted = insertIntoNodes(blocks, placement.parentId, cloneBlock(operation.block), placement.index);
            if (!inserted.inserted)
                return invalidMutation("The insertion target changed while applying the mutation.", operationIndex);
            document = withRootBlocks(document, placement.root, inserted.nodes);
            diff.push({ op: "insert", id: operation.block.id, to: placement });
            continue;
        }
        const located = findLocatedBlock(document, operation.id);
        if (!located)
            return { ok: false, conflict: { code: "block_not_found", id: operation.id, operationIndex } };
        if (operation.op === "remove") {
            const blocks = blocksForRoot(document, located.placement.root);
            if (!blocks)
                return invalidMutation("The removed block root is no longer writable.", operationIndex);
            const removed = removeFromNodes(blocks, operation.id);
            if (!removed.removed)
                return invalidMutation("The removed block disappeared while applying the mutation.", operationIndex);
            document = withRootBlocks(document, located.placement.root, removed.nodes);
            diff.push({ op: "remove", id: operation.id, from: located.placement });
            continue;
        }
        if (operation.op === "move") {
            if (operation.placement.parentId !== null && containsId(located.node, operation.placement.parentId)) {
                return { ok: false, conflict: { code: "cycle", id: operation.id, parentId: operation.placement.parentId, operationIndex } };
            }
            const sourceBlocks = blocksForRoot(document, located.placement.root);
            if (!sourceBlocks)
                return invalidMutation("The moved block root is no longer writable.", operationIndex);
            const removed = removeFromNodes(sourceBlocks, operation.id);
            if (!removed.removed)
                return invalidMutation("The moved block disappeared while applying the mutation.", operationIndex);
            const withoutSource = withRootBlocks(document, located.placement.root, removed.nodes);
            const targetBlocks = blocksForRoot(withoutSource, operation.placement.root);
            if (targetBlocks === null) {
                return { ok: false, conflict: { code: "invalid_placement", operationIndex, message: "Chrome mutations require an existing custom chrome slot." } };
            }
            if (operation.placement.parentId !== null && !findInRoot(withoutSource, operation.placement.root, operation.placement.parentId)) {
                return { ok: false, conflict: { code: "invalid_placement", operationIndex, message: "The move parent must exist in the destination root." } };
            }
            const count = siblingCount(withoutSource, operation.placement);
            const placement = count === null ? null : resolvedPlacement(operation.placement, count);
            if (!placement) {
                return { ok: false, conflict: { code: "invalid_placement", operationIndex, message: "The move index is outside the post-removal sibling list." } };
            }
            const inserted = insertIntoNodes(targetBlocks, placement.parentId, removed.removed, placement.index);
            if (!inserted.inserted)
                return invalidMutation("The move target changed while applying the mutation.", operationIndex);
            document = withRootBlocks(withoutSource, placement.root, inserted.nodes);
            const nextLocated = findLocatedBlock(document, operation.id);
            if (!nextLocated)
                return invalidMutation("The moved block was not retained in the document.", operationIndex);
            const unchanged = sameRoot(nextLocated.placement.root, located.placement.root)
                && nextLocated.placement.parentId === located.placement.parentId
                && nextLocated.placement.index === located.placement.index;
            if (!unchanged)
                diff.push({ op: "move", id: operation.id, from: located.placement, to: nextLocated.placement });
            continue;
        }
        const existingProps = (located.node.props ?? {});
        if (operation.op === "set") {
            const hadPrevious = Object.hasOwn(existingProps, operation.key);
            const previous = existingProps[operation.key];
            if (hadPrevious && previous !== undefined && equalJson(previous, operation.value))
                continue;
            const replacement = {
                ...located.node,
                props: { ...existingProps, [operation.key]: cloneJson(operation.value) },
            };
            const rootBlocks = blocksForRoot(document, located.placement.root);
            if (!rootBlocks)
                return invalidMutation("The set target root is no longer writable.", operationIndex);
            const replaced = replaceInNodes(rootBlocks, operation.id, replacement);
            if (!replaced)
                return invalidMutation("The set target disappeared while applying the mutation.", operationIndex);
            document = withRootBlocks(document, located.placement.root, replaced);
            diff.push({
                op: "set",
                id: operation.id,
                key: operation.key,
                hadPrevious,
                ...(hadPrevious ? { previous: cloneJson(previous) } : {}),
                value: cloneJson(operation.value),
            });
            continue;
        }
        if (!Object.hasOwn(existingProps, operation.key))
            continue;
        const previous = existingProps[operation.key];
        const nextProps = { ...existingProps };
        delete nextProps[operation.key];
        const replacement = { ...located.node, ...(Object.keys(nextProps).length === 0 ? {} : { props: nextProps }) };
        if (Object.keys(nextProps).length === 0)
            delete replacement.props;
        const rootBlocks = blocksForRoot(document, located.placement.root);
        if (!rootBlocks)
            return invalidMutation("The reset target root is no longer writable.", operationIndex);
        const replaced = replaceInNodes(rootBlocks, operation.id, replacement);
        if (!replaced)
            return invalidMutation("The reset target disappeared while applying the mutation.", operationIndex);
        document = withRootBlocks(document, located.placement.root, replaced);
        diff.push({ op: "reset", id: operation.id, key: operation.key, previous: cloneJson(previous) });
    }
    if (diff.length > 0 && state.revision === Number.MAX_SAFE_INTEGER) {
        return invalidState("The document revision cannot advance beyond the safe integer range.");
    }
    return {
        ok: true,
        previousRevision: state.revision,
        state: {
            revision: diff.length > 0 ? state.revision + 1 : state.revision,
            document,
        },
        diff,
    };
}
/**
 * Validates and applies the one explicit recovery/import operation. It remains
 * revisioned and rejects malformed theme/chrome or unstable legacy IDs, so a
 * host can expose recovery without reviving an unbounded document PATCH path.
 */
export function applyPigmentDocumentImport(state, replacement) {
    // Unlike normal semantic edits, recovery deliberately permits an older host
    // envelope with invalid IDs/theme/chrome. Its revision remains the concurrency
    // token; only the *replacement* must satisfy the current Pigment contract.
    if (!isRevision(state.revision)) {
        return { ok: false, conflict: { code: "invalid_state", message: "Expected a revisioned document with a safe non-negative revision." } };
    }
    if (!isPigmentDocumentImport(replacement)) {
        return { ok: false, conflict: { code: "invalid_mutation", message: "Expected a well-formed Pigment document import." } };
    }
    if (replacement.expectedRevision !== state.revision) {
        return { ok: false, conflict: { code: "revision_mismatch", expectedRevision: replacement.expectedRevision, actualRevision: state.revision } };
    }
    if (state.revision === Number.MAX_SAFE_INTEGER) {
        return { ok: false, conflict: { code: "invalid_state", message: "The document revision cannot advance beyond the safe integer range." } };
    }
    return {
        ok: true,
        previousRevision: state.revision,
        state: {
            revision: state.revision + 1,
            document: structuredClone(replacement.document),
        },
        diff: [{ op: "import", schema: "pigment.document-import.v1" }],
    };
}
